import { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { loadBriefStatus } from '@/hooks/useBriefStatus';
import { briefId } from './inboxUtils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PAGE_SIZE = 500;   // first load and each "Load older" page
const POLL_SIZE = 50;           // newest briefs re-checked on every poll
const POLL_MS = 60_000;

const byNewest = (a, b) => String(b.discovered_at || '').localeCompare(String(a.discovered_at || ''));

function fetchPage(limit, offset = 0) {
  return axios.get(`${API}/dashboard/attacks`, {
    params: { severity: 'all', limit, offset },
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
  });
}

/** Merge fresh rows into the list by attack id (fresh wins), newest first. */
function merge(current, fresh) {
  const map = new Map(current.map((b) => [briefId(b), b]));
  fresh.forEach((b) => map.set(briefId(b), b));
  return [...map.values()].sort(byNewest);
}

/**
 * The Inbox brief list.
 *
 * Loads the newest PAGE_SIZE briefs, then every minute re-reads only the
 * newest POLL_SIZE and merges them, so a long history is not re-downloaded
 * each minute. Polling pauses while the tab is hidden. The shared triage
 * state is refreshed on the same timer so teammates' changes show up.
 *
 * `error` is a failed first load (nothing to show); `refreshError` is a
 * failed poll while older data is still on screen.
 */
export default function useInboxBriefs() {
  const [briefs, setBriefs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshError, setRefreshError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const loadedRef = useRef(false);
  const lastUpdatedRef = useRef(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchPage(PAGE_SIZE);
      const rows = Array.isArray(res.data) ? res.data : [];
      setBriefs(rows.slice().sort(byNewest));
      setHasMore(rows.length >= PAGE_SIZE);
      loadedRef.current = true;
      lastUpdatedRef.current = Date.now();
      setLastUpdated(new Date());
      setRefreshError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(async () => {
    if (!loadedRef.current) return load();
    try {
      const res = await fetchPage(POLL_SIZE);
      const rows = Array.isArray(res.data) ? res.data : [];
      setBriefs((cur) => merge(cur, rows));
      lastUpdatedRef.current = Date.now();
      setLastUpdated(new Date());
      setRefreshError(null);
    } catch (e) {
      setRefreshError(e);
    }
    loadBriefStatus();
    return undefined;
  }, [load]);

  const loadOlder = useCallback(async () => {
    setLoadingMore(true);
    try {
      const res = await fetchPage(PAGE_SIZE, briefs.length);
      const rows = Array.isArray(res.data) ? res.data : [];
      setBriefs((cur) => merge(cur, rows));
      setHasMore(rows.length >= PAGE_SIZE);
    } finally {
      setLoadingMore(false);
    }
  }, [briefs.length]);

  /** Load the whole history (used when a deep link points at an older brief). */
  const loadAll = useCallback(async () => {
    setLoadingMore(true);
    try {
      const res = await fetchPage(5000);
      const rows = Array.isArray(res.data) ? res.data : [];
      setBriefs((cur) => merge(cur, rows));
      setHasMore(false);
    } finally {
      setLoadingMore(false);
    }
  }, []);

  useEffect(() => {
    load();
    const tick = () => { if (!document.hidden) refresh(); };
    const t = setInterval(tick, POLL_MS);
    const onVisible = () => {
      if (!document.hidden && Date.now() - lastUpdatedRef.current > POLL_MS) refresh();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load, refresh]);

  return { briefs, loading, error, refreshError, lastUpdated, hasMore, loadingMore, reload: load, refresh, loadOlder, loadAll };
}
